/**
 * Verbes métier de la sous-traitance.
 *
 * Lire est N1 : la base ferme tout au terrain. Écrire est N2 — rien ne sort
 * de l'entreprise, et chaque geste se défait. Les rôles sont dits ici pour
 * refuser proprement avant l'appel, et en base pour que le refus tienne.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type {
  AddSubcontractorDocumentInput,
  ArchiveSubcontractorInput,
  ListSubcontractorsInput,
  OpenSubcontractPlanningInput,
  RemoveSubcontractInput,
  RemoveSubcontractorDocumentInput,
  RemoveSubcontractInvoiceInput,
  RemoveSubcontractPaymentInput,
  RevokeSubcontractPlanningInput,
  SaveSubcontractInput,
  SaveSubcontractInvoiceInput,
  SaveSubcontractPaymentInput,
  SaveSubcontractorInput,
  UpdateSubcontractorDocumentInput,
} from "./schema.js";
import { engagedHtBySubcontractor, subcontractorDocumentsDue } from "./documents.js";
import type { DocumentDue, SubcontractorDocument } from "./documents.js";
import { invoiceAmounts, invoiceBalance, subcontractAccount } from "./invoices.js";
import type { SubcontractInvoice, SubcontractPayment } from "./invoices.js";
import { clientPaysSubcontractor, contractsNeedingAttention } from "./approval.js";
import type { ContractNeedingAttention, SubcontractPlanningLink } from "./approval.js";
import type { ProjectSubcontract, Subcontractor } from "./types.js";
import type { VigilanceBasis } from "../config/types.js";

export type SubcontractorDetails = Omit<SaveSubcontractorInput, "confirmationId" | "subcontractorId">;
export type SubcontractDetails = Omit<SaveSubcontractInput, "confirmationId" | "subcontractId">;

export interface NewSubcontractorDocument {
  subcontractorId: string;
  kind: SubcontractorDocument["kind"];
  label: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  storagePath: string | null;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  notes: string | null;
}

export type SubcontractInvoiceDetails = Omit<SaveSubcontractInvoiceInput, "confirmationId" | "invoiceId">;
export type SubcontractPaymentDetails = Omit<SaveSubcontractPaymentInput, "confirmationId" | "paymentId">;

/** Les factures et règlements d'un chantier, ou d'un seul marché. */
export interface SubcontractInvoicing {
  invoices: SubcontractInvoice[];
  payments: SubcontractPayment[];
}

export interface SubcontractorDocumentDates {
  label: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  notes: string | null;
}

export interface SubcontractingRepository {
  listSubcontractors(ctx: ExecutionContext, includeArchived: boolean): Promise<Subcontractor[]>;
  saveSubcontractor(
    ctx: ExecutionContext,
    id: string | undefined,
    details: SubcontractorDetails,
  ): Promise<Subcontractor>;
  setSubcontractorArchived(ctx: ExecutionContext, id: string, archived: boolean): Promise<void>;

  saveSubcontract(
    ctx: ExecutionContext,
    id: string | undefined,
    details: SubcontractDetails,
  ): Promise<ProjectSubcontract>;
  removeSubcontract(ctx: ExecutionContext, id: string): Promise<void>;
  findSubcontract(ctx: ExecutionContext, id: string): Promise<ProjectSubcontract | null>;
  /** Les marchés à signer ou signés de tous les chantiers, avec le nom du chantier. */
  listOpenContracts(ctx: ExecutionContext): Promise<(ProjectSubcontract & { projectName: string })[]>;

  /** Qui a un marché, et dans quel état : de quoi savoir qui relancer. */
  listContractStatuses(
    ctx: ExecutionContext,
  ): Promise<{ subcontractorId: string; status: string; amountHt: number }[]>;
  /** Les pièces de tout le carnet : quelques dizaines de lignes au plus. */
  listDocuments(ctx: ExecutionContext): Promise<SubcontractorDocument[]>;
  addDocument(ctx: ExecutionContext, doc: NewSubcontractorDocument): Promise<SubcontractorDocument>;
  updateDocument(ctx: ExecutionContext, id: string, dates: SubcontractorDocumentDates): Promise<void>;
  /** Retire la ligne et son fichier. */
  removeDocument(ctx: ExecutionContext, id: string): Promise<void>;

  listInvoicing(
    ctx: ExecutionContext,
    filter: { projectId: string } | { subcontractId: string },
  ): Promise<SubcontractInvoicing>;
  /** Le marché désigné porte le chantier : le dépôt le recopie. */
  saveInvoice(
    ctx: ExecutionContext,
    id: string | undefined,
    details: SubcontractInvoiceDetails,
  ): Promise<SubcontractInvoice>;
  /** Retire la facture et ses règlements. */
  removeInvoice(ctx: ExecutionContext, id: string): Promise<void>;
  savePayment(
    ctx: ExecutionContext,
    id: string | undefined,
    details: SubcontractPaymentDetails,
  ): Promise<SubcontractPayment>;
  removePayment(ctx: ExecutionContext, id: string): Promise<void>;

  /**
   * Ouvre le lien de planning d'un marché et rend le jeton **en clair, une
   * seule fois**. Passe par `open_subcontract_planning()` : le jeton et son
   * échéance naissent en base.
   */
  openPlanningLink(ctx: ExecutionContext, subcontractId: string): Promise<string>;
  listPlanningLinks(ctx: ExecutionContext, projectId: string): Promise<SubcontractPlanningLink[]>;
  revokePlanningLink(ctx: ExecutionContext, id: string): Promise<void>;
}

const ordered = (issuedOn: string | null, expiresOn: string | null) => {
  if (issuedOn && expiresOn && expiresOn < issuedOn) {
    throw new DomainError("invalid_input", "La fin de validité ne peut pas précéder la date de la pièce.", {});
  }
};

const BUREAU = ["patron", "conducteur", "assistant"] as const;

const euros = (v: number) => `${v.toFixed(2).replace(".", ",")} €`;

/** Pour le calcul des montants seulement : l'échéance n'y joue pas. */
const SANS_ECHEANCE = "9999-12-31";

export function createSubcontractingServices(repo: SubcontractingRepository) {
  const listerSousTraitants = defineService<ListSubcontractorsInput, Subcontractor[]>({
    name: "lister_sous_traitants",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.listSubcontractors(ctx, input.includeArchived),
  });

  const enregistrerSousTraitant = defineService<SaveSubcontractorInput, Subcontractor>({
    name: "enregistrer_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) =>
      repo.saveSubcontractor(ctx, input.subcontractorId, {
        name: input.name,
        trade: input.trade ?? null,
        siret: input.siret ?? null,
        contactName: input.contactName ?? null,
        phone: input.phone ?? null,
        email: input.email ?? null,
        address: input.address ?? null,
        legalForm: input.legalForm ?? null,
        registration: input.registration ?? null,
        signatoryName: input.signatoryName ?? null,
        signatoryRole: input.signatoryRole ?? null,
        isSme: input.isSme ?? null,
        bankName: input.bankName ?? null,
        iban: input.iban ?? null,
        notes: input.notes ?? null,
      }),
  });

  const archiverSousTraitant = defineService<ArchiveSubcontractorInput, void>({
    name: "archiver_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.setSubcontractorArchived(ctx, input.subcontractorId, input.archived),
  });

  /**
   * Confier un lot, une phase ou une tâche à un sous-traitant — ou modifier
   * ce qui l'était. Un lot **ou** une phase, pas les deux : une phase d'un
   * lot confié l'est déjà. Sans l'un ni l'autre, la tâche doit être décrite.
   */
  const confierSousTraitance = defineService<SaveSubcontractInput, ProjectSubcontract>({
    name: "confier_sous_traitance",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => {
      const details: SubcontractDetails = {
        projectId: input.projectId,
        subcontractorId: input.subcontractorId,
        lotId: input.lotId ?? null,
        phaseId: input.phaseId ?? null,
        scope: input.scope ?? null,
        amountHt: input.amountHt,
        status: input.status ?? "draft",
        signedOn: input.signedOn ?? null,
        approvalStatus: input.approvalStatus ?? "not_presented",
        presentedOn: null,
        approvalDecidedOn: null,
        paymentGuarantee: input.paymentGuarantee ?? "none",
        publicContractRef: input.publicContractRef ?? null,
        priceRevision: input.priceRevision ?? null,
        advanceRequested: input.advanceRequested ?? false,
        notes: input.notes ?? null,
      };
      // Une date ne survit pas à l'état qui la justifie : pas présenté, pas
      // de présentation ; pas décidé, pas de décision.
      if (details.approvalStatus !== "not_presented") details.presentedOn = input.presentedOn ?? null;
      if (details.approvalStatus === "approved" || details.approvalStatus === "refused") {
        details.approvalDecidedOn = input.approvalDecidedOn ?? null;
      }
      if (details.presentedOn && details.approvalDecidedOn && details.approvalDecidedOn < details.presentedOn) {
        throw new DomainError(
          "invalid_input",
          "La réponse du client ne peut pas précéder la présentation du sous-traitant.",
          {},
        );
      }
      if (details.lotId && details.phaseId) {
        throw new DomainError(
          "invalid_input",
          "Confiez un lot ou une phase, pas les deux : une phase d'un lot confié l'est déjà.",
          {},
        );
      }
      if (!details.lotId && !details.phaseId && !details.scope) {
        throw new DomainError("invalid_input", "Dites ce qui est confié : un lot, une phase ou une tâche décrite.", {});
      }
      return repo.saveSubcontract(ctx, input.subcontractId, details);
    },
  });

  /** Retirer un marché saisi par erreur. Un marché qui n'a pas eu lieu s'annule. */
  const retirerSousTraitance = defineService<RemoveSubcontractInput, void>({
    name: "retirer_sous_traitance",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: (ctx, input) => repo.removeSubcontract(ctx, input.subcontractId),
  });

  const listerPiecesSousTraitants = defineService<Record<string, never>, SubcontractorDocument[]>({
    name: "lister_pieces_sous_traitants",
    level: ActionLevel.Read,
    handler: (ctx) => repo.listDocuments(ctx),
  });

  /**
   * Enregistrer une pièce reçue. Le fichier est déjà dans le Storage ; s'il
   * manque, la pièce vaut note — « reçue par courrier, classée au bureau ».
   */
  const deposerPieceSousTraitant = defineService<AddSubcontractorDocumentInput, SubcontractorDocument>({
    name: "deposer_piece_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => {
      ordered(input.issuedOn ?? null, input.expiresOn ?? null);
      const path = input.storagePath ?? null;
      if (path && !path.startsWith(`${ctx.tenantId}/sous-traitants/${input.subcontractorId}/`)) {
        throw new DomainError("invalid_input", "Le fichier n'est pas rangé dans le dossier de ce sous-traitant.", {});
      }
      return repo.addDocument(ctx, {
        subcontractorId: input.subcontractorId,
        kind: input.kind,
        label: input.label ?? null,
        issuedOn: input.issuedOn ?? null,
        expiresOn: input.expiresOn ?? null,
        storagePath: path,
        fileName: path ? (input.fileName ?? null) : null,
        mimeType: path ? (input.mimeType ?? null) : null,
        sizeBytes: path ? (input.sizeBytes ?? null) : null,
        notes: input.notes ?? null,
      });
    },
  });

  /** Corriger les dates ou la précision d'une pièce. Le fichier, lui, ne change pas. */
  const corrigerPieceSousTraitant = defineService<UpdateSubcontractorDocumentInput, void>({
    name: "corriger_piece_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => {
      ordered(input.issuedOn ?? null, input.expiresOn ?? null);
      return repo.updateDocument(ctx, input.documentId, {
        label: input.label ?? null,
        issuedOn: input.issuedOn ?? null,
        expiresOn: input.expiresOn ?? null,
        notes: input.notes ?? null,
      });
    },
  });

  const retirerPieceSousTraitant = defineService<RemoveSubcontractorDocumentInput, void>({
    name: "retirer_piece_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.removeDocument(ctx, input.documentId),
  });

  /**
   * Les pièces à réclamer aujourd'hui, pour les sous-traitants qui ont un
   * marché en cours. Ce que le tableau de bord affiche. Le seuil de 5 000 € HT
   * suit le réglage du patron, que l'appelant a lu.
   */
  const piecesAReclamer = defineService<
    { today: string; applyVigilanceThreshold?: boolean; vigilanceBasis?: VigilanceBasis; confirmationId?: string },
    DocumentDue[]
  >({
    name: "pieces_a_reclamer",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const [carnet, marches, pieces] = await Promise.all([
        repo.listSubcontractors(ctx, false),
        repo.listContractStatuses(ctx),
        repo.listDocuments(ctx),
      ]);
      return subcontractorDocumentsDue(
        carnet,
        marches,
        pieces,
        input.today,
        input.applyVigilanceThreshold ?? false,
        input.vigilanceBasis ?? "cumulative",
      );
    },
  });

  /**
   * Ce que le seuil de 5 000 € compare, par sous-traitant, sur ses marchés en
   * cours : leur cumul HT, ou le plus gros d'entre eux.
   */
  const montantsEngages = defineService<
    { vigilanceBasis?: VigilanceBasis; confirmationId?: string },
    Record<string, number>
  >({
    name: "montants_engages_sous_traitants",
    level: ActionLevel.Read,
    handler: async (ctx, input) =>
      Object.fromEntries(
        engagedHtBySubcontractor(await repo.listContractStatuses(ctx), input.vigilanceBasis ?? "cumulative"),
      ),
  });

  /**
   * Les marchés en cours auxquels il manque l'agrément du client ou la
   * garantie de paiement (loi de 1975). Ce que le tableau de bord affiche.
   */
  const marchesAMettreEnRegle = defineService<{ confirmationId?: string }, ContractNeedingAttention[]>({
    name: "marches_a_mettre_en_regle",
    level: ActionLevel.Read,
    handler: async (ctx) => contractsNeedingAttention(await repo.listOpenContracts(ctx)),
  });

  const listerFacturesSousTraitance = defineService<
    { projectId: string; confirmationId?: string },
    SubcontractInvoicing
  >({
    name: "lister_factures_sous_traitance",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.listInvoicing(ctx, { projectId: input.projectId }),
  });

  /**
   * Enregistrer une facture reçue d'un sous-traitant, ou la corriger. En
   * franchise, pas de taux : la facture n'a pas de TVA. Une facture déjà
   * réglée ne peut pas descendre sous ce qui lui a été payé.
   */
  const enregistrerFactureSousTraitant = defineService<SaveSubcontractInvoiceInput, SubcontractInvoice>({
    name: "enregistrer_facture_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: async (ctx, input) => {
      const details: SubcontractInvoiceDetails = {
        subcontractId: input.subcontractId,
        invoiceNumber: input.invoiceNumber,
        invoiceDate: input.invoiceDate,
        dueDate: input.dueDate ?? null,
        amountHt: input.amountHt,
        vatMode: input.vatMode ?? "autoliquidation",
        vatRate: input.vatMode === "franchise" ? 0 : (input.vatRate ?? 20),
        retentionPercent: input.retentionPercent ?? 5,
        notes: input.notes ?? null,
      };
      if (details.dueDate && details.dueDate < details.invoiceDate) {
        throw new DomainError("invalid_input", "L'échéance ne peut pas précéder la date de la facture.", {});
      }
      if (input.invoiceId) {
        const { payments } = await repo.listInvoicing(ctx, { subcontractId: input.subcontractId });
        const regle = invoiceBalance({ ...details, id: input.invoiceId, dueDate: null }, payments, SANS_ECHEANCE).paid;
        const net = invoiceAmounts(details).net;
        if (regle > net) {
          throw new DomainError(
            "invalid_input",
            `Cette facture a déjà été réglée de ${euros(regle)} : son net à payer ne peut pas descendre à ${euros(net)}.`,
            {},
          );
        }
      }
      return repo.saveInvoice(ctx, input.invoiceId, details);
    },
  });

  /** Retirer une facture saisie par erreur, avec ses règlements. */
  const retirerFactureSousTraitant = defineService<RemoveSubcontractInvoiceInput, void>({
    name: "retirer_facture_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: (ctx, input) => repo.removeInvoice(ctx, input.invoiceId),
  });

  /**
   * Enregistrer un règlement : d'une facture, jamais au-delà de son reste à
   * payer ; ou de la retenue rendue, jamais au-delà de ce qui est gardé.
   * Payé par le client, seulement sous délégation ou paiement direct.
   */
  const enregistrerReglementSousTraitant = defineService<SaveSubcontractPaymentInput, SubcontractPayment>({
    name: "enregistrer_reglement_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: async (ctx, input) => {
      const kind = input.kind ?? "invoice";
      const invoiceId = kind === "invoice" ? (input.invoiceId ?? null) : null;
      const paidBy = input.paidBy ?? "company";
      if (paidBy === "client") {
        const marche = await repo.findSubcontract(ctx, input.subcontractId);
        if (!marche || !clientPaysSubcontractor(marche.paymentGuarantee)) {
          throw new DomainError(
            "invalid_input",
            "Le client ne paie le sous-traitant que sous délégation de paiement ou paiement direct : dites-le sur le marché.",
            {},
          );
        }
      }
      const { invoices, payments } = await repo.listInvoicing(ctx, { subcontractId: input.subcontractId });
      // Le règlement qu'on corrige ne compte pas contre lui-même.
      const autres = payments.filter((p) => p.id !== input.paymentId);
      if (kind === "invoice") {
        const facture = invoices.find((i) => i.id === invoiceId);
        if (!facture) {
          throw new DomainError("invalid_input", "Choisissez la facture réglée, parmi celles de ce marché.", {});
        }
        const reste = invoiceBalance(facture, autres, SANS_ECHEANCE).remaining;
        if (input.amount > reste) {
          throw new DomainError(
            "invalid_input",
            `Il ne reste que ${euros(reste)} à payer sur la facture ${facture.invoiceNumber}.`,
            {},
          );
        }
      } else {
        const gardee = subcontractAccount(
          { id: input.subcontractId, amountHt: 0 },
          invoices,
          autres,
          SANS_ECHEANCE,
        ).retentionHeld;
        if (input.amount > gardee) {
          throw new DomainError(
            "invalid_input",
            `La retenue encore gardée sur ce marché est de ${euros(gardee)}.`,
            {},
          );
        }
      }
      return repo.savePayment(ctx, input.paymentId, {
        subcontractId: input.subcontractId,
        kind,
        invoiceId,
        amount: input.amount,
        paidOn: input.paidOn,
        paidBy,
        notes: input.notes ?? null,
      });
    },
  });

  const retirerReglementSousTraitant = defineService<RemoveSubcontractPaymentInput, void>({
    name: "retirer_reglement_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: (ctx, input) => repo.removePayment(ctx, input.paymentId),
  });

  /**
   * Ouvrir le lien de planning d'un marché, pour le sous-traitant qui n'a pas
   * de compte. Rouvrir révoque le précédent. Rien ne part : c'est le patron
   * qui transmet le lien.
   */
  const partagerPlanningSousTraitant = defineService<OpenSubcontractPlanningInput, { token: string }>({
    name: "partager_planning_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: async (ctx, input) => ({ token: await repo.openPlanningLink(ctx, input.subcontractId) }),
  });

  const listerPlanningsPartages = defineService<
    { projectId: string; confirmationId?: string },
    SubcontractPlanningLink[]
  >({
    name: "lister_plannings_partages",
    level: ActionLevel.Read,
    allowedRoles: [...BUREAU, "lecture_seule"],
    handler: (ctx, input) => repo.listPlanningLinks(ctx, input.projectId),
  });

  /** Fermer le lien : immédiat, et la trace reste. */
  const revoquerPlanningSousTraitant = defineService<RevokeSubcontractPlanningInput, void>({
    name: "revoquer_planning_sous_traitant",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.revokePlanningLink(ctx, input.linkId),
  });

  return {
    marchesAMettreEnRegle,
    partagerPlanningSousTraitant,
    listerPlanningsPartages,
    revoquerPlanningSousTraitant,
    listerFacturesSousTraitance,
    enregistrerFactureSousTraitant,
    retirerFactureSousTraitant,
    enregistrerReglementSousTraitant,
    retirerReglementSousTraitant,
    piecesAReclamer,
    montantsEngages,
    listerPiecesSousTraitants,
    deposerPieceSousTraitant,
    corrigerPieceSousTraitant,
    retirerPieceSousTraitant,
    listerSousTraitants,
    enregistrerSousTraitant,
    archiverSousTraitant,
    confierSousTraitance,
    retirerSousTraitance,
  };
}

export type SubcontractingServices = ReturnType<typeof createSubcontractingServices>;
